import { useCallback, useMemo, useState } from 'react';
import {
  buildEntityLabelOptions,
  filterItemsByEntityLabel,
  getLabelsForNewItemFromActiveFilter,
  normalizeEntityLabelName,
  validateEntityLabelName,
} from '../utils/entityLabelFilter';

/**
 * Per–object-type label filter state (labels are not shared across entity types).
 *
 * When creating items while a label chip is active, pass `labelsForNewItem` into the
 * create API so the new row appears in the filtered view (not for All or Unlabeled).
 *
 * @param {{ items: unknown[], labelField?: string, scopeKey?: string }} options — `labelField` may be a label array (`labels`) or a scalar string (`category`).
 * `scopeKey` resets the active chip and not-yet-saved label names when the list identity changes (for example a different Quick Analysis file).
 */
export function useEntityLabelFilter({ items, labelField = 'labels', scopeKey = '' }) {
  const [activeLabel, setActiveLabel] = useState(null);
  const [extraLabelNames, setExtraLabelNames] = useState([]);
  const [seenScopeKey, setSeenScopeKey] = useState(scopeKey);
  if (seenScopeKey !== scopeKey) {
    setSeenScopeKey(scopeKey);
    setActiveLabel(null);
    setExtraLabelNames([]);
  }

  const labelOptions = useMemo(
    () => buildEntityLabelOptions(items, extraLabelNames, labelField),
    [extraLabelNames, items, labelField]
  );

  const filteredItems = useMemo(
    () => filterItemsByEntityLabel(items, activeLabel, labelField),
    [activeLabel, items, labelField]
  );

  const labelsForNewItem = useMemo(
    () => getLabelsForNewItemFromActiveFilter(activeLabel),
    [activeLabel]
  );

  const addLabelName = useCallback((name) => {
    const validated = validateEntityLabelName(name);
    if (!validated.ok) return validated;
    setExtraLabelNames((prev) => {
      if (prev.includes(validated.label)) return prev;
      return [...prev, validated.label].sort((a, b) => a.localeCompare(b));
    });
    return { ok: true, label: validated.label };
  }, []);

  const renameLabelName = useCallback((oldName, newName) => {
    const oldTrimmed = normalizeEntityLabelName(oldName);
    const validated = validateEntityLabelName(newName);
    if (!validated.ok) return validated;
    setExtraLabelNames((prev) => {
      const next = prev
        .filter((name) => name !== oldTrimmed)
        .concat(validated.label)
        .sort((a, b) => a.localeCompare(b));
      return [...new Set(next)];
    });
    setActiveLabel((prev) => (prev === oldTrimmed ? validated.label : prev));
    return { ok: true, label: validated.label };
  }, []);

  const removeLabelName = useCallback((name) => {
    const trimmed = normalizeEntityLabelName(name);
    if (!trimmed) return { ok: false, error: 'Label name is required' };
    setExtraLabelNames((prev) => prev.filter((entry) => entry !== trimmed));
    setActiveLabel((prev) => (prev === trimmed ? null : prev));
    return { ok: true };
  }, []);

  return {
    activeLabel,
    setActiveLabel,
    labelOptions,
    filteredItems,
    labelsForNewItem,
    addLabelName,
    renameLabelName,
    removeLabelName,
  };
}

export function getEntityListItemId(item) {
  return item?.id ?? item?._id ?? '';
}

/** Lightweight row selection for compact entity lists (checkbox column + bulk actions). */
export function useEntityListSelection(getItemId = getEntityListItemId) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const handleSelectAll = useCallback(
    (e, items) => {
      const list = Array.isArray(items) ? items : [];
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (e.target.checked) {
          list.forEach((item) => {
            const id = getItemId(item);
            if (id) next.add(id);
          });
        } else {
          list.forEach((item) => {
            const id = getItemId(item);
            if (id) next.delete(id);
          });
        }
        return next;
      });
    },
    [getItemId]
  );

  const handleSelectOne = useCallback((id, checked) => {
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  return {
    selectedIds,
    setSelectedIds,
    handleSelectAll,
    handleSelectOne,
    clearSelection,
  };
}
